import React, { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Receipt, 
  FileText, 
  Package, 
  DollarSign,
  Building2,
  Calendar,
  User,
  Plus,
  Trash2
} from 'lucide-react';
import { useProcurement } from '@/context/ProcurementContext';
import { formatCurrency } from '@/lib/utils';
import { toast } from 'sonner';
import { DebitNote, DebitNoteItem, Invoice, PurchaseOrder } from '@/data/mockData';

interface DebitNotesFormProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (debitNote: Partial<DebitNote>) => Promise<boolean>;
  linkedPO?: PurchaseOrder;
  linkedInvoice?: Invoice;
  mode: 'po' | 'invoice';
}

const REJECTION_REASONS = [
  'Damaged items during delivery',
  'Wrong quantity delivered',
  'Price mismatch',
  'Quality issues',
  'Wrong items delivered',
  'Late delivery',
  'Packaging issues',
  'Specification mismatch',
  'Other'
];

export const DebitNotesForm: React.FC<DebitNotesFormProps> = ({
  isOpen,
  onClose,
  onSave,
  linkedPO,
  linkedInvoice,
  mode
}) => {
  const { state } = useProcurement();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formData, setFormData] = useState({
    reason: '',
    notes: '',
    items: [] as DebitNoteItem[]
  });

  useEffect(() => {
    if (isOpen) {
      // Initialize form with linked document data
      const items: DebitNoteItem[] = [];
      
      if (mode === 'po' && linkedPO) {
        linkedPO.items.forEach((item, index) => {
          items.push({
            id: index + 1,
            skuId: item.skuId,
            skuName: item.skuName,
            skuCode: item.skuCode,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            totalPrice: item.totalPrice,
            rejectedQuantity: 0,
            rejectionReason: ''
          });
        });
      } else if (mode === 'invoice' && linkedInvoice) {
        linkedInvoice.items.forEach((item, index) => {
          items.push({
            id: index + 1,
            skuId: item.skuId,
            skuName: item.skuName,
            skuCode: item.skuCode,
            quantity: item.quantity,
            unitPrice: item.unitPrice,
            totalPrice: item.totalPrice,
            rejectedQuantity: 0,
            rejectionReason: ''
          });
        });
      }

      setFormData({
        reason: '',
        notes: '',
        items
      });
    }
  }, [isOpen, linkedPO, linkedInvoice, mode]);

  const handleItemChange = (index: number, field: keyof DebitNoteItem, value: any) => {
    const updatedItems = [...formData.items];
    updatedItems[index] = {
      ...updatedItems[index],
      [field]: value
    };

    // Recalculate total price if quantity or unit price changes
    if (field === 'rejectedQuantity' || field === 'unitPrice') {
      const item = updatedItems[index];
      updatedItems[index].totalPrice = item.rejectedQuantity * item.unitPrice;
    }

    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const removeItem = (index: number) => {
    const updatedItems = formData.items.filter((_, i) => i !== index);
    setFormData(prev => ({
      ...prev,
      items: updatedItems
    }));
  };

  const handleSubmit = async () => {
    if (!formData.reason.trim()) {
      toast.error('Please select a rejection reason');
      return;
    }

    const rejectedItems = formData.items.filter(item => item.rejectedQuantity > 0);
    if (rejectedItems.length === 0) {
      toast.error('Please specify rejected quantities for at least one item');
      return;
    }

    const totalAmount = rejectedItems.reduce((sum, item) => sum + item.totalPrice, 0);

    const debitNoteData: Partial<DebitNote> = {
      debitNoteNumber: `DN-${new Date().getFullYear()}-${String(Date.now()).slice(-6)}`,
      linkedPOId: mode === 'po' ? linkedPO?.id : undefined,
      linkedInvoiceId: mode === 'invoice' ? linkedInvoice?.id : undefined,
      linkedPONumber: mode === 'po' ? linkedPO?.poNumber : undefined,
      linkedInvoiceNumber: mode === 'invoice' ? linkedInvoice?.invoiceNumber : undefined,
      vendorId: mode === 'po' ? linkedPO?.vendorId : linkedInvoice?.vendorId,
      vendorName: mode === 'po' ? 
        state.vendors.find(v => v.id === linkedPO?.vendorId)?.name || 'Unknown Vendor' :
        linkedInvoice?.vendorName || 'Unknown Vendor',
      reason: formData.reason,
      items: rejectedItems,
      totalAmount,
      status: 'Open',
      createdAt: new Date().toISOString(),
      createdBy: state.currentUser?.id || 1,
      createdByName: state.currentUser?.name || 'System User',
      notes: formData.notes
    };

    try {
      setIsSubmitting(true);
      const success = await onSave(debitNoteData);
      if (success) {
        toast.success('Debit note created successfully');
        onClose();
      } else {
        toast.error('Failed to create debit note');
      }
    } catch (error) {
      console.error('Error creating debit note:', error);
      toast.error('Failed to create debit note');
    } finally {
      setIsSubmitting(false);
    }
  };

  const linkedDocument = mode === 'po' ? linkedPO : linkedInvoice;
  const documentType = mode === 'po' ? 'Purchase Order' : 'Invoice';
  const documentNumber = mode === 'po' ? linkedPO?.poNumber : linkedInvoice?.invoiceNumber;

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="h-5 w-5" />
            Generate Debit Note
          </DialogTitle>
          <DialogDescription>
            Create a debit note for rejected items from {documentType} {documentNumber}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Linked Document Info */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <FileText className="h-5 w-5" />
                Linked {documentType}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground">{documentType} Number</p>
                  <p className="font-semibold">{documentNumber}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Vendor</p>
                  <p className="font-semibold">
                    {mode === 'po' ? 
                      state.vendors.find(v => v.id === linkedPO?.vendorId)?.name || 'Unknown' :
                      linkedInvoice?.vendorName || 'Unknown'
                    }
                  </p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Total Items</p>
                  <p className="font-semibold">{formData.items.length}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground">Original Amount</p>
                  <p className="font-semibold">
                    {formatCurrency(linkedDocument?.totalAmount || 0)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Rejection Reason */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg">Rejection Details</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <Label htmlFor="reason">Reason for Rejection *</Label>
                <Select value={formData.reason} onValueChange={(value) => 
                  setFormData(prev => ({ ...prev, reason: value }))
                }>
                  <SelectTrigger>
                    <SelectValue placeholder="Select rejection reason" />
                  </SelectTrigger>
                  <SelectContent>
                    {REJECTION_REASONS.map((reason) => (
                      <SelectItem key={reason} value={reason}>
                        {reason}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div>
                <Label htmlFor="notes">Additional Notes</Label>
                <Textarea
                  id="notes"
                  placeholder="Enter any additional details about the rejection..."
                  value={formData.notes}
                  onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                  rows={3}
                />
              </div>
            </CardContent>
          </Card>

          {/* Items Table */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <Package className="h-5 w-5" />
                Rejected Items
              </CardTitle>
              <CardDescription>
                Specify the quantity and reason for each rejected item
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Item</TableHead>
                      <TableHead>SKU Code</TableHead>
                      <TableHead>Original Qty</TableHead>
                      <TableHead>Unit Price</TableHead>
                      <TableHead>Rejected Qty</TableHead>
                      <TableHead>Item Reason</TableHead>
                      <TableHead>Total Value</TableHead>
                      <TableHead>Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {formData.items.map((item, index) => (
                      <TableRow key={item.id}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{item.skuName}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline">{item.skuCode}</Badge>
                        </TableCell>
                        <TableCell>{item.quantity}</TableCell>
                        <TableCell>{formatCurrency(item.unitPrice)}</TableCell>
                        <TableCell>
                          <Input
                            type="number"
                            min="0"
                            max={item.quantity}
                            value={item.rejectedQuantity}
                            onChange={(e) => handleItemChange(index, 'rejectedQuantity', parseInt(e.target.value) || 0)}
                            className="w-20"
                          />
                        </TableCell>
                        <TableCell>
                          <Input
                            placeholder="Reason for rejection"
                            value={item.rejectionReason}
                            onChange={(e) => handleItemChange(index, 'rejectionReason', e.target.value)}
                            className="min-w-[150px]"
                          />
                        </TableCell>
                        <TableCell>
                          <span className="font-medium">
                            {formatCurrency(item.totalPrice)}
                          </span>
                        </TableCell>
                        <TableCell>
                          {formData.items.length > 1 && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => removeItem(index)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>

          {/* Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="text-lg flex items-center gap-2">
                <DollarSign className="h-5 w-5" />
                Debit Note Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
                <div className="text-center p-4 bg-red-50 dark:bg-red-950 rounded-lg">
                  <p className="text-2xl font-bold text-red-600">
                    {formData.items.filter(item => item.rejectedQuantity > 0).length}
                  </p>
                  <p className="text-sm text-muted-foreground">Rejected Items</p>
                </div>
                <div className="text-center p-4 bg-orange-50 dark:bg-orange-950 rounded-lg">
                  <p className="text-2xl font-bold text-orange-600">
                    {formData.items.reduce((sum, item) => sum + item.rejectedQuantity, 0)}
                  </p>
                  <p className="text-sm text-muted-foreground">Total Rejected Qty</p>
                </div>
                <div className="text-center p-4 bg-blue-50 dark:bg-blue-950 rounded-lg">
                  <p className="text-2xl font-bold text-blue-600">
                    {formatCurrency(formData.items.reduce((sum, item) => sum + item.totalPrice, 0))}
                  </p>
                  <p className="text-sm text-muted-foreground">Debit Amount</p>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Action Buttons */}
          <div className="flex justify-end gap-2 pt-4">
            <Button variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button 
              onClick={handleSubmit}
              disabled={isSubmitting}
              className="flex items-center gap-2"
            >
              <Receipt className="h-4 w-4" />
              {isSubmitting ? 'Creating...' : 'Create Debit Note'}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
